import { useEffect, useRef, useState } from "react";

import aboutPoster from "@/assets/images/user/aboutPoster.jpg";
import programArt from "@/assets/images/user/programArt.svg";
import programFesta from "@/assets/images/user/programFesta.svg";
import programFocus from "@/assets/images/user/programFocus.svg";
import programPlay from "@/assets/images/user/programPlay.svg";
import programStage from "@/assets/images/user/programStage.svg";
import ProgramAccordion from "@/components/user/common/ProgramAccordion";
import SubTitle from "@/components/user/common/SubTitle";
import TitleBox from "@/components/user/common/TitleBox";

interface ProgramSummaryGroup {
    label?: string;
    items: React.ReactNode[];
}

// 글자 크기 전환점(md)과 리스트 스타일 전환점을 반드시 동일하게 유지할 것.
// md 미만 = 13.5px 불릿(dot) 목록 / md 이상 = 24px " I " 구분자 flex-wrap 나열
// leading 은 1.5(=20.25px) 대신 정수 20px.
// 시안(Figma) 구조: 콘텐츠 박스 패딩 16px → 텍스트 노드 → 그 안에서 글자가 20.25px 들여쓰기.
// 즉 ms 는 0 이고(텍스트 노드는 패딩 끝에서 시작), 들여쓰기는 pl 20px 하나로 표현한다.
// dot 은 그 20px 여백의 좌우 정중앙에 놓는다(양옆 8.1px 균등, 시안의 "dot 영역" 그대로).
// dot 의 화면상 x 는 pl + before:left 로 정해진다. 여백 가운데를 유지하려면
// left = -(pl - 3.8)/2 - 3.95 ≒ -16px (pl 20px 기준). pl 을 바꾸면 left 도 다시 계산할 것.
// md 이상은 dot 목록이 아니므로 pl 을 0 으로 되돌린다.
// md 이상은 flex-wrap 컨테이너. inline 나열로 두면 "GV2026" 처럼 항목 안의 공백에서 줄이 끊긴다.
// flex item 은 기본 폭이 max-content 라 항목 단위로만 넘어가므로 br 없이 반응형이 유지된다.
const summaryListClass =
    "list-none pl-[20px] font-['GongGothicLight'] text-[13.5px] leading-[20px] tracking-[-0.25px] text-black md:flex md:flex-wrap md:pl-0 md:text-[24px] md:leading-[1.8]";
// dot 은 배경색 div 가 아니라 본문 폰트(esamanru)의 가운뎃점(U+00B7) 글리프로 그린다.
// div 로 그리면 조상 요소의 소수점 y 오프셋 탓에 4px 원이 픽셀 경계를 걸쳐 번지고,
// 네이티브 ::marker 는 dot 지름·세로위치·글자간격이 marker font-size 하나에 묶여 따로 못 잡는다
//   (marker 13.5px = 지름 3.8px·간격 5.8px, 10px 로 줄이면 간격은 4.8px 이지만 dot 이 1.5px 내려앉음).
// 폰트가 U+2022(•) 를 갖고 있지 않아 · 를 쓴다. 25px 일 때 잉크 지름이 3.8px 로 마커와 같다.
// 조절 지점: 지름=font-size, 세로위치=top(1px 이면 글자 중심과 일치),
// left 의 절대값이 pl(20px) 보다 커지면 dot 이 목록 바깥으로 삐져나오니 주의.
// md 이상에서는 dot 을 지우고 " I " 구분자로 전환. 구분자는 항목 안(after)에 있어 항목과 함께 이동한다.
const summaryItemClass =
    "relative before:absolute before:left-[-16px] before:top-[1px] before:text-[25px] before:leading-[20px] before:content-['·'] md:before:content-none md:after:mx-[8px] md:after:content-['_I_'] md:last:after:content-none";

/** 프로그램 요약 목록 */
function ProgramSummary({ groups }: { groups: ProgramSummaryGroup[] }) {
    return (
        <div className="flex flex-col gap-[16px] md:gap-[24px]">
            {groups.map((group, groupIdx) => (
                <div key={groupIdx} className="flex flex-col gap-[8px]">
                    {group.label && (
                        <p className="font-['GongGothicBold'] text-[16px] leading-[1.5] tracking-[-0.25px] text-[#FF5B2E] md:text-[24px]">
                            {group.label}
                        </p>
                    )}
                    <ul className={summaryListClass}>
                        {group.items.map((item, itemIdx) => (
                            <li key={itemIdx} className={summaryItemClass}>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

const programList = [
    {
        title: "무대 프로그램",
        background: "bg-program202501",
        img: programStage,
        imgClassName: "md:h-[36px]",
        contents: (
            <ProgramSummary
                groups={[
                      {
                        label: "STREET STAGE PRE-EVENT",
                        items: [
                            <>
                                GXG 버스킹 타임 : 게임사 직장인 밴드 공연 <br />
                                📍판교 콘텐츠 거리
                            </>,
                        ],
                    },
                    {
                        label: "MAIN STAGE DAY1",
                        items: [
                            "사전 공연",
                            "GXG 2026 개막선언 & 축하공연",
                            <>
                                The 3<sup className="text-[0.65em]">rd</sup> GXG SOUND TRACK
                            </>,
                        ],
                    },
                    {
                        label: "MAIN STAGE DAY2",
                        items: ["일러스타 페스 스테이지", "AI GSDC 시상식", "GXG 갈라 콘서트"],
                    },
                ]}
            />
        ),
    },
    {
        title: "체험 프로그램",
        background: "bg-program202502",
        img: programPlay,
        imgClassName: "md:h-[36px]",
        contents: (
            <ProgramSummary
                groups={[
                    {
                        items: [
                            "GXG 코스튬 체험존",
                            "GXG 플레이 라운지",
                            "브랜드 체험존",
                            "Cool Station",
                            "GXG 캠퍼스 아케이드",
                        ],
                    },
                ]}
            />
        ),
    },
    {
        title: "전시 프로그램",
        background: "bg-program202503",
        img: programArt,
        imgClassName: "md:h-[36px]",
        contents: (
            <ProgramSummary
                groups={[
                    {
                        items: [
                            // 협업사 표기는 프로그램명보다 한 단계 작게. md 미만은 목록 기본 크기(13.5px) 그대로 둔다.
                            <>
                                GXG 코스튬 갤러리<span className="md:text-[18px]">｜스마일게이트 X RZCOS</span>
                            </>,
                            "GXG 사회공헌 전시",
                            "GXG SOUND TRACK 전시",
                        ],
                    },
                ]}
            />
        ),
    },
    {
        title: "네트워킹 프로그램",
        background: "bg-program202504",
        img: programFocus,
        imgClassName: "md:h-[36px]",
        contents: (
            <ProgramSummary
                groups={[
                    {
                        items: ["Connect Fair K-game 수출상담회", "Game Tech Night with GXG and Microsoft"],
                    },
                ]}
            />
        ),
    },
    {
        title: "GXG 콜라보레이션",
        background: "bg-program202505",
        img: programFesta,
        imgClassName: "md:h-[36px]",
        contents: (
            <ProgramSummary
                groups={[
                    {
                        items: [
                            "인디크래프트",
                            "일러스타 페스",
                            "한국 서브컬처 공모전",
                            "AI GSDC",
                            "GV2026",
                            "포켓몬 카드 게임 2026 성남CITY 배틀 토너먼트",
                            "2026 경기게임마이스터고 졸업 작품 전시회",
                        ],
                    },
                ]}
            />
        ),
    },
];

const infoList = [
    {
        label: "행사명",
        value: (
            <>
                GXG 2026 <br className="md:hidden" />
                <span className="text-[14px] md:text-[22px]">(Game culture X Generation 2026)</span>
            </>
        ),
        valueClass: "leading-[1.5]",
    },
    { label: <>일&nbsp;&nbsp;&nbsp;&nbsp;정</>, value: "9월 11일(금)~12일(토), 2일 간" },
    { label: <>장&nbsp;&nbsp;&nbsp;&nbsp;소</>, value: "경기도 성남시 판교역 일대" },
    { label: <>주&nbsp;&nbsp;&nbsp;&nbsp;최</>, value: "성남시" },
    { label: <>주&nbsp;&nbsp;&nbsp;&nbsp;관</>, value: "성남산업진흥원, 게임문화재단" },
    {
        label: <>후&nbsp;&nbsp;&nbsp;&nbsp;원</>,
        value: (
            <>
                문화체육관광부, 한국콘텐츠진흥원, <br className="md:hidden" />게임물관리위원회, 경기도, <br className="hidden md:block" />
                경기관광공사,<br className="md:hidden" /> 넥슨코리아, 엔씨, 마이크로소프트, <br className="md:hidden" />월드클래스, <br className="hidden md:block" />
                이나씨씨씨오, 창조공작소,<br className="md:hidden" /> 펄어비스, 플레이샵, 호요버스 코리아
            </>
        ),
        isStart: true,
        // isStart: false,
        // valueClass: "text-[15px]",
    },
];

export default function About() {
    const sect2Ref = useRef<HTMLDivElement>(null);
    const [sect2On, setSect2On] = useState(false);

    //스크롤시 section on
    const scrollSectOn = () => {
        const scroll = window.scrollY;
        const sections: {
            ref: React.RefObject<HTMLDivElement>;
            onSet: React.Dispatch<React.SetStateAction<boolean>>;
        }[] = [{ ref: sect2Ref, onSet: setSect2On }];

        sections.forEach(({ ref, onSet }) => {
            if (ref.current) {
                const offsetTop = ref.current.offsetTop;
                if (scroll >= offsetTop - 100) {
                    onSet(true);
                }
            }
        });
    };

    useEffect(() => {
        window.addEventListener("scroll", scrollSectOn);

        return () => {
            window.removeEventListener("scroll", scrollSectOn);
        };
    }, []);

    return (
        <div className="px-[20px] xl:px-0">
            <section>
                <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-[20px] p-[8px_0_80px] text-center font-['GongGothicMedium'] text-[18px] md:gap-[60px] md:p-[40px_0_160px] md:text-[28px] [&_p>span]:text-primary leading-[29px] md:[&_p]:leading-[45px]">
                    <div>
                        <p className="text-[28px] md:text-[48px]">
                            Welcome to <span>GXG 2026</span>
                        </p>
                        <p className="text-[24px] md:text-[32px] mt-2 md:mt-6 mb-5">게임, 문화로 즐기다</p>
                    </div>
                    <p>
                        게임을 플레이하는 순간을 넘어,
                        <br />
                        게임이 만들어내는 문화와 <br className="md:hidden" />
                        사람들의 이야기가 만나는 축제!
                    </p>
                    <p>
                        GXG 2026은 <br className="md:hidden" />
                        음악, 아트, 스토리, 퍼포먼스 등<br />
                        게임을 둘러싼 다양한 문화 콘텐츠를 통해
                        <br />
                        세대와 장르를 넘어 모두가 함께 즐기고 <br className="md:hidden" />
                        공감하는 <span>도심형 게임문화축제</span>입니다.
                    </p>
                    <p>
                        올해 더욱 풍성해진 콘텐츠와 <br className="md:hidden" />
                        새로운 경험으로,
                        <br />
                        게임이 문화가 되는 순간을{" "}
                        <br className="md:hidden" />
                        <span>GXG 2026</span>에서 함께 만나보세요.
                    </p>
                </div>
            </section>
            <section className="mx-auto w-full max-w-[1400px] pb-[120px] md:pb-[160px]" ref={sect2Ref}>
                <TitleBox title="ABOUT" text="GXG 2026 소개" />
                <div className="pt-[40px] md:pt-[60px] xl:flex xl:gap-[48px]">
                    <div className={`xl:opacity-0${sect2On ? " xl:animate-slide-in-from-bottom-30" : ""}`}>
                        <img src={aboutPoster} alt="GXG 2025" />
                    </div>
                    <div
                        className={`w-full pt-[40px] md:pt-[60px] xl:flex-1 xl:pt-0 xl:opacity-0${sect2On ? " xl:animate-slide-in-from-right" : ""}`}
                    >
                        <ul className="flex flex-col gap-[12px] md:gap-[16px] xl:h-full xl:justify-between">
                            {infoList.map((item, idx) => (
                                <li
                                    key={idx}
                                    className={`flex ${item.isStart ? "items-start" : "items-center"} gap-[12px] md:gap-[16px]`}
                                >
                                    <p className="w-full max-w-[70px] rounded-[8px] border border-transparent py-[8px] text-center font-['GongGothicMedium'] text-[18px] font-[500] leading-[28px] text-[#000] shadow-[0_0_12px_0_rgba(221,54,98,0.20)] [background-clip:padding-box,border-box] [background-image:linear-gradient(#fff,#fff),linear-gradient(135deg,#E64C88_0%,#3734C3_100%)] [background-origin:border-box] md:max-w-[140px] md:py-[18px] md:text-[22px]">
                                        {item.label}
                                    </p>
                                    <p
                                        className={`flex-1 font-['GongGothicLight'] font-medium text-black md:text-[24px] ${item.valueClass || "text-[16px] leading-[1.8]"}`}
                                    >
                                        {item.value}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <SubTitle title="프로그램" className="p-[40px_0_20px] md:p-[60px_0_40px]" />
                <ProgramAccordion
                    list={programList}
                    design="2026"
                    className="gap-[24px]"
                    triggerClassName="[&>p]:text-[18px] [&>p]:md:text-[32px]"
                    contentClassName="p-[16px] md:p-[40px]"
                    defaultOpenItems={[0, 1, 2, 3, 4]}
                    hideItemBorder
                />
            </section>
        </div>
    );
}
